// Single getUserMedia owner. Multiple trackers (face/hands/pose) share one
// MediaStream + one <video> element so we don't double-tax the GPU.
//
// Sub-agent contract:
//   const cam = new Camera();
//   await cam.start();           // throws on permission denial
//   const video = cam.video;     // bound to the live stream
//   cam.onFrame(() => { ... });  // notify subscribers each rAF tick

export interface CameraOptions {
  /** Preferred resolution; UA may downsample. */
  width?: number;
  height?: number;
  facingMode?: 'user' | 'environment';
}

export class Camera {
  readonly video: HTMLVideoElement;
  private stream: MediaStream | null = null;
  private started = false;
  private readonly opts: Required<CameraOptions>;
  private readonly subscribers = new Set<() => void>();
  private rafId: number | null = null;

  constructor(opts: CameraOptions = {}) {
    this.opts = {
      width: opts.width ?? 640,
      height: opts.height ?? 480,
      facingMode: opts.facingMode ?? 'user',
    };
    this.video = document.createElement('video');
    this.video.autoplay = true;
    this.video.playsInline = true;
    this.video.muted = true;
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        width: { ideal: this.opts.width },
        height: { ideal: this.opts.height },
        facingMode: this.opts.facingMode,
      },
    });
    this.video.srcObject = this.stream;
    await this.video.play();
    this.started = true;
    this.tick();
  }

  stop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.stream != null) {
      for (const t of this.stream.getTracks()) t.stop();
      this.stream = null;
    }
    this.video.srcObject = null;
    this.started = false;
  }

  onFrame(cb: () => void): () => void {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  get isReady(): boolean {
    return this.started && this.video.readyState >= 2;
  }

  get width(): number {
    return this.video.videoWidth || this.opts.width;
  }

  get height(): number {
    return this.video.videoHeight || this.opts.height;
  }

  private tick = (): void => {
    if (!this.started) return;
    if (this.isReady) {
      for (const cb of this.subscribers) cb();
    }
    this.rafId = requestAnimationFrame(this.tick);
  };
}
